import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { formatDate, formatCurrency } from '../utils/dateUtils';
import { useOwner } from '../hooks/useOwner';
import toast from 'react-hot-toast';
import { Shield, Receipt, Wrench, Bell, ArrowRight, CheckCircle, ExternalLink } from 'lucide-react';
import { isNative } from '../utils/native';
import { PhoneHero } from '../components/common/PhoneHero';

// Only ever show the host of a pay link, so a link added by a team member can be recognised
// (or doubted) before it is opened.
const linkHost = (u) => { try { return new URL(u).hostname; } catch { return null; } };

const getStatus = (dateStr) => {
  if (!dateStr) return { status: 'missing', label: 'Missing', color: '#6b7280', days: null };
  const days = Math.floor((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0) return { status: 'expired', label: `Expired ${Math.abs(days)}d ago`, color: '#ef4444', days };
  if (days <= 7) return { status: 'critical', label: `${days}d left`, color: '#ef4444', days };
  if (days <= 30) return { status: 'warning', label: `${days}d left`, color: '#f97316', days };
  if (days <= 60) return { status: 'soon', label: `${days}d left`, color: '#eab308', days };
  return { status: 'valid', label: 'Valid', color: '#22c55e', days };
};

const STAT_CARDS = [
  { key: 'all',      label: 'Total Alerts',       grad: ['#4f8ef7','#7c3aed'] },
  { key: 'urgent',   label: 'Expired / Critical',  grad: ['#ef4444','#dc2626'] },
  { key: 'missing',  label: 'Missing Records',     grad: ['#6b7280','#4b5563'] },
  { key: 'upcoming', label: 'Upcoming (≤60d)',      grad: ['#f97316','#eab308'] },
];

const TYPE_FILTERS = [
  { key: 'all',        label: 'All Types',   icon: Bell },
  { key: 'insurance',  label: 'Insurance',   icon: Shield },
  { key: 'road_tax',   label: 'Road Tax',    icon: Receipt },
  { key: 'mechanical', label: 'Mechanical',  icon: Wrench },
  { key: 'service',    label: 'Service',     icon: Wrench },
];

const typeLabel = { insurance: 'Insurance', road_tax: 'Road Tax', mechanical: 'Mechanical', service: 'Service' };
const typeIcon  = { insurance: Shield, road_tax: Receipt, mechanical: Wrench, service: Wrench };

export const Alerts = () => {
  const [vehicles, setVehicles]   = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [roadTax, setRoadTax]     = useState([]);
  const [mechanical, setMechanical] = useState([]);
  const [services, setServices]   = useState([]);
  const [unpaid, setUnpaid]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const { role, isOwner, isAdmin } = useOwner();
  const canSettle = isOwner || isAdmin || role === 'editor';
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter]     = useState('all');
  const navigate = useNavigate();

  const loadUnpaid = async () => {
    const { data } = await supabase
      .from('expenses')
      .select('*, vehicles(id, registration_number, brand, model)')
      .eq('payment_status', 'unpaid')
      .order('expense_date', { ascending: true });
    setUnpaid(data || []);
  };

  const markPaid = async (id) => {
    const { error } = await supabase.from('expenses').update({ payment_status: 'paid', paid_at: new Date().toISOString() }).eq('id', id);
    if (error) { toast.error('Could not update: ' + error.message); return; }
    toast.success('Marked as paid');
    loadUnpaid();
  };

  useEffect(() => {
    loadUnpaid();
    const load = async () => {
      const [v, i, r, m, s] = await Promise.all([
        supabase.from('vehicles').select('*, vehicle_photos(*)').in('status', ['active','inactive','classic','daneira','immobilized','dokimi_test','service','restoration']),
        supabase.from('insurance_records').select('*').order('expiry_date', { ascending: true }),
        supabase.from('road_tax_records').select('*').order('expiry_date', { ascending: true }),
        supabase.from('mechanical_reports').select('*').order('next_due_date', { ascending: true }),
        supabase.from('service_records').select('*').order('next_service_date', { ascending: true }),
      ]);
      setVehicles(v.data || []);
      setInsurance(i.data || []);
      setRoadTax(r.data || []);
      setMechanical(m.data || []);
      setServices(s.data || []);
      setLoading(false);
    };
    load();
  }, []);

  // Build alerts
  const buildAlerts = () => {
    const alerts = [];
    vehicles.forEach(v => {
      // Insurance
      const ins = insurance.filter(r => r.vehicle_id === v.id).sort((a, b) => new Date(b.expiry_date) - new Date(a.expiry_date));
      if (ins.length === 0) {
        alerts.push({ vehicleId: v.id, reg: v.registration_number, brand: v.brand, model: v.model, photo: v.vehicle_photos?.[0]?.url || null, type: 'insurance', title: 'No insurance record', date: null, detail: null, ...getStatus(null) });
      } else {
        const s = getStatus(ins[0].expiry_date);
        if (s.status !== 'valid') alerts.push({ vehicleId: v.id, reg: v.registration_number, brand: v.brand, model: v.model, photo: v.vehicle_photos?.[0]?.url || null, type: 'insurance', title: 'Insurance expiry', date: ins[0].expiry_date, detail: ins[0].insurance_company || null, ...s });
      }
      // Road Tax
      // Road Tax — skip for immobilized vehicles (off the road, no tax required)
      const rt = roadTax.filter(r => r.vehicle_id === v.id).sort((a, b) => new Date(b.expiry_date) - new Date(a.expiry_date));
      const rtImmobilized = v.status === 'immobilized' || v.is_immobilized;
      if (rtImmobilized) {
        // no road tax alert for immobilized vehicles
      } else if (rt.length === 0) {
        alerts.push({ vehicleId: v.id, reg: v.registration_number, brand: v.brand, model: v.model, photo: v.vehicle_photos?.[0]?.url || null, type: 'road_tax', title: 'No road tax record', date: null, detail: null, ...getStatus(null) });
      } else {
        const s = getStatus(rt[0].expiry_date);
        if (s.status !== 'valid') alerts.push({ vehicleId: v.id, reg: v.registration_number, brand: v.brand, model: v.model, photo: v.vehicle_photos?.[0]?.url || null, type: 'road_tax', title: 'Road tax expiry', date: rt[0].expiry_date, detail: null, ...s });
      }
      // Mechanical
      const mech = mechanical.filter(r => r.vehicle_id === v.id).sort((a, b) => new Date(b.next_due_date) - new Date(a.next_due_date));
      if (mech.length > 0 && mech[0].next_due_date) {
        const s = getStatus(mech[0].next_due_date);
        if (s.status !== 'valid') alerts.push({ vehicleId: v.id, reg: v.registration_number, brand: v.brand, model: v.model, photo: v.vehicle_photos?.[0]?.url || null, type: 'mechanical', title: 'Mechanical report due', date: mech[0].next_due_date, detail: mech[0].garage_name || null, ...s });
      }
      // Service
      const svc = services.filter(r => r.vehicle_id === v.id).sort((a, b) => new Date(b.next_service_date) - new Date(a.next_service_date));
      if (svc.length > 0 && svc[0].next_service_date) {
        const s = getStatus(svc[0].next_service_date);
        if (s.status !== 'valid') alerts.push({ vehicleId: v.id, reg: v.registration_number, brand: v.brand, model: v.model, photo: v.vehicle_photos?.[0]?.url || null, type: 'service', title: 'Service due', date: svc[0].next_service_date, detail: svc[0].garage_name || null, ...s });
      }
    });
    const order = { expired: 0, critical: 1, missing: 2, warning: 3, soon: 4 };
    return alerts.sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));
  };

  const allAlerts = buildAlerts();

  const counts = {
    all:      allAlerts.length,
    urgent:   allAlerts.filter(a => ['expired','critical'].includes(a.status)).length,
    missing:  allAlerts.filter(a => a.status === 'missing').length,
    upcoming: allAlerts.filter(a => ['warning','soon'].includes(a.status)).length,
  };

  const filtered = allAlerts.filter(a => {
    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'urgent'   && ['expired','critical'].includes(a.status)) ||
      (statusFilter === 'missing'  && a.status === 'missing') ||
      (statusFilter === 'upcoming' && ['warning','soon'].includes(a.status));
    const matchType = typeFilter === 'all' || a.type === typeFilter;
    return matchStatus && matchType;
  });

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="page">
      {isNative && (
        <PhoneHero
          sub="Here's what needs your attention today"
          chips={[
            `${allAlerts.length} to review`,
            ...(counts.urgent > 0 ? [{ text: `${counts.urgent} urgent`, tone: 'down' }] : []),
            ...(unpaid.length > 0 ? [`${unpaid.length} unpaid invoice${unpaid.length !== 1 ? 's' : ''}`] : []),
          ]}
        />
      )}
      {!isNative && <div className="page-header">
        <div>
          <div className="page-title">Alerts</div>
          <div className="page-subtitle">Action centre — {allAlerts.length} items need attention across your fleet</div>
        </div>
      </div>}

      {/* Invoices waiting for payment */}
      {unpaid.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid rgba(249,115,22,0.4)', borderRadius: 16, padding: 18, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Invoices awaiting payment</div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>
              {unpaid.length} invoice{unpaid.length !== 1 ? 's' : ''} · <strong style={{ color: '#f97316' }}>{formatCurrency(unpaid.reduce((t, r) => t + Number(r.amount || 0), 0))}</strong>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {unpaid.map(r => {
              const host = r.payment_link ? linkHost(r.payment_link) : null;
              const overdue = r.due_date && new Date(r.due_date) < new Date(new Date().setHours(0, 0, 0, 0));
              return (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '12px 14px', borderRadius: 12, background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.25)' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 16, fontFamily: 'DM Mono, monospace', cursor: 'pointer' }} onClick={() => navigate(`/vehicles/${r.vehicles?.id || r.vehicle_id}`)}>{r.vehicles?.registration_number}</span>
                      <span style={{ fontSize: 13, color: 'var(--text3)' }}>{r.vehicles?.brand} {r.vehicles?.model}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>
                      {r.supplier || 'Supplier not set'}{r.description ? ` — ${r.description}` : ''}
                    </div>
                    <div style={{ fontSize: 12, color: overdue ? '#ef4444' : 'var(--text3)', marginTop: 2 }}>
                      Invoice {formatDate(r.expense_date)}{r.invoice_number ? ` · #${r.invoice_number}` : ''}{r.due_date ? ` · ${overdue ? 'was due' : 'pay by'} ${formatDate(r.due_date)}` : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#f97316' }}>{formatCurrency(Number(r.amount || 0))}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {r.receipt_url && (
                      <a href={r.receipt_url} target="_blank" rel="noreferrer noopener" className="btn btn-ghost btn-sm"><ExternalLink size={12} /> Invoice</a>
                    )}
                    {host && (
                      <a href={r.payment_link} target="_blank" rel="noreferrer noopener" className="btn btn-primary btn-sm" title={`Opens ${host}`}>Pay</a>
                    )}
                    {canSettle && (
                      <button className="btn btn-ghost btn-sm" onClick={() => markPaid(r.id)}><CheckCircle size={12} /> Mark as paid</button>
                    )}
                  </div>
                  {host && <div style={{ flexBasis: '100%', fontSize: 11, color: 'var(--text3)' }}>The Pay button opens <strong>{host}</strong>. Only pay if you recognise it.</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stat cards — matching dashboard style */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        {STAT_CARDS.map(card => {
          const count = counts[card.key];
          const isActive = statusFilter === card.key;
          const isEmpty = count === 0;
          const grad = isEmpty ? ['#22c55e','#16a34a'] : card.grad;
          return (
            <div
              key={card.key}
              onClick={() => setStatusFilter(isActive ? 'all' : card.key)}
              style={{
                background: 'var(--surface)',
                border: `1px solid ${isActive ? grad[0] + '55' : 'var(--border)'}`,
                borderRadius: 16,
                padding: 20,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: isActive
                  ? `0 8px 24px ${grad[0]}33`
                  : '0 2px 8px rgba(0,0,0,0.2)',
              }}
            >

              {/* Small dot accent */}
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
                marginBottom: 14,
                boxShadow: `0 0 10px ${grad[0]}88`,
              }} />

              {/* Count */}
              <div style={{
                fontSize: 32, fontWeight: 900, letterSpacing: -1,
                background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                backgroundClip: 'text', lineHeight: 1, marginBottom: 6,
              }}>
                {count}
              </div>

              {/* Label */}
              <div style={{
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: 0.8, color: 'var(--text3)',
              }}>
                {card.label}
              </div>

              {/* Active bottom bar */}
              {isActive && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
                  background: `linear-gradient(90deg, ${grad[0]}, ${grad[1]})`,
                  borderRadius: '0 0 16px 16px',
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Type filter pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {TYPE_FILTERS.map(t => {
          const isActive = typeFilter === t.key;
          const count = t.key === 'all' ? allAlerts.length : allAlerts.filter(a => a.type === t.key).length;
          return (
            <button
              key={t.key}
              onClick={() => setTypeFilter(t.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '8px 16px', borderRadius: 20, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 500,
                transition: 'all 0.15s',
                border: `1px solid ${isActive ? '#4f8ef7' : 'var(--border)'}`,
                background: isActive ? 'rgba(79,142,247,0.12)' : 'var(--surface)',
                color: isActive ? '#4f8ef7' : 'var(--text3)',
                boxShadow: isActive ? '0 4px 12px rgba(79,142,247,0.2)' : 'none',
              }}
            >
              <t.icon size={13} />
              {t.label}
              <span style={{
                padding: '1px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                background: isActive ? '#4f8ef7' : 'var(--surface2)',
                color: isActive ? 'white' : 'var(--text3)',
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Alert list */}
      {filtered.length === 0 ? (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 16, padding: '60px 20px', textAlign: 'center',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(6,182,212,0.15))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <CheckCircle size={28} style={{ color: '#22c55e' }} />
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: 'var(--text)' }}>All clear!</h3>
          <p style={{ color: 'var(--text3)', fontSize: 13 }}>No alerts in this category. Your fleet is in great shape.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((a, i) => {
            const Icon = typeIcon[a.type] || Bell;
            return (
              <div
                key={i}
                onClick={() => navigate(`/vehicles/${a.vehicleId}`)}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 14,
                  padding: '16px 18px',
                  display: 'flex', alignItems: 'center', gap: 14,
                  cursor: 'pointer', transition: 'all 0.2s',
                  position: 'relative', overflow: 'hidden',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = a.color + '55';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = `0 8px 24px ${a.color}22`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Blurred glow — top right */}
                <div style={{
                  position: 'absolute', top: -20, right: -20,
                  width: 80, height: 80, borderRadius: '50%',
                  background: `radial-gradient(circle, ${a.color}22 0%, transparent 70%)`,
                  filter: 'blur(10px)', pointerEvents: 'none',
                }} />

                {/* Left colour bar */}
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
                  background: a.color, borderRadius: '14px 0 0 14px',
                  opacity: 0.8,
                }} />

                {/* Vehicle photo or icon */}
                <div style={{
                  width: 44, height: 44, borderRadius: 11, overflow: 'hidden',
                  flexShrink: 0, background: `${a.color}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${a.color}30`,
                }}>
                  {a.photo
                    ? <img src={a.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Icon size={20} style={{ color: a.color }} />
                  }
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 17, fontFamily: 'DM Mono, monospace' }}>
                      {a.reg}
                    </span>
                    <span style={{ color: 'var(--text3)', fontSize: 13 }}>
                      {a.brand} {a.model}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: a.detail || a.date ? 4 : 0 }}>
                    {a.title}
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {a.detail && <span style={{ fontSize: 13, color: 'var(--text2)' }}>📍 {a.detail}</span>}
                    {a.date   && <span style={{ fontSize: 13, color: 'var(--text2)' }}>📅 {formatDate(a.date)}</span>}
                  </div>
                </div>

                {/* Right badges */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    background: 'rgba(79,142,247,0.12)', color: '#4f8ef7',
                    border: '1px solid rgba(79,142,247,0.25)',
                  }}>
                    {typeLabel[a.type]}
                  </span>
                  <span style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                    background: `${a.color}18`, color: a.color,
                    border: `1px solid ${a.color}44`,
                  }}>
                    {a.label}
                  </span>
                </div>

                <ArrowRight size={15} style={{ color: 'var(--text3)', flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};